import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AUTO_ADVANCE_DELAY_MS } from "@/modules/session-survey/consts/autoAdvanceDelay";
import { isAutoAdvanceQuestion } from "@/modules/session-survey/consts/isAutoAdvanceQuestion";
import { SurveyEvents } from "@/modules/session-survey/consts/surveyEvents";
import { SessionSurveyContext } from "@/modules/session-survey/context/SessionSurveyContext";
import { useSessionSurveyMutation } from "@/modules/session-survey/hooks/useSessionSurveyMutation";
import { submitSurveyResponses } from "@/modules/session-survey/services/submitSurveyResponses";
import { SessionSurveyContextType } from "@/modules/session-survey/types/SessionSurveyContextType";
import { SurveyAnswerValue } from "@/modules/session-survey/types/SurveyAnswerValue";
import { SurveyDetails } from "@/modules/session-survey/types/SurveyDetails";
import { SurveyQuestion } from "@/modules/session-survey/types/SurveyQuestion";
import { SurveyResponse } from "@/modules/session-survey/types/SurveyResponse";
import { captureEvent } from "@/modules/shared/utils/posthog";

interface SessionSurveyProviderProps {
  details: SurveyDetails;
  children: ReactNode;
  submitResponses?: typeof submitSurveyResponses;
}

const isAnsweredValue = (
  value: SurveyAnswerValue | undefined,
): value is SurveyAnswerValue =>
  value !== undefined && (typeof value !== "string" || value.trim().length > 0);

const satisfiesRequirement = (
  question: SurveyQuestion,
  value: SurveyAnswerValue | undefined,
) => !question.required || isAnsweredValue(value);

export const SessionSurveyProvider = ({
  details,
  children,
  submitResponses = submitSurveyResponses,
}: SessionSurveyProviderProps) => {
  const surveyId = details.surveyId;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<
    Record<string, SurveyAnswerValue | undefined>
  >({});
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const hasStartedRef = useRef(false);
  const hasShownRef = useRef(false);
  const lastCapturedAnswersRef = useRef(new Map<string, SurveyAnswerValue>());

  const questions = useMemo(
    () =>
      [...details.definition.questions].sort(
        (first, second) => first.order - second.order,
      ),
    [details.definition.questions],
  );

  const currentQuestion = questions[currentIndex];
  const totalSteps = questions.length;
  const isLastStep = currentIndex === totalSteps - 1;
  const canGoBack = currentIndex > 0;
  const currentAnswer = answers[currentQuestion.id];
  const hasCurrentAnswer = isAnsweredValue(currentAnswer);
  const canGoForward = satisfiesRequirement(currentQuestion, currentAnswer);
  const hasAnswers = useMemo(
    () => Object.values(answers).some(isAnsweredValue),
    [answers],
  );
  // Submit mirrors forward navigation: only a required, unanswered question
  // blocks. A member may finish having skipped any optional question.
  const canSubmit = isLastStep && canGoForward;

  const handleSubmitSuccess = useCallback(() => {
    setIsCompleted(true);
    captureEvent(SurveyEvents.Completed, { survey_id: surveyId });
  }, [surveyId]);

  const handleSubmitError = useCallback(() => {
    setHasSubmitError(true);
  }, []);

  const { isPending, mutate: mutateResponses } = useSessionSurveyMutation(
    surveyId,
    {
      onSuccess: handleSubmitSuccess,
      onError: handleSubmitError,
    },
    submitResponses,
  );

  useEffect(() => {
    if (hasShownRef.current) return;

    hasShownRef.current = true;
    captureEvent(SurveyEvents.Shown, { survey_id: surveyId });
  }, [surveyId]);

  useEffect(() => {
    if (!hasAnswers || isCompleted) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Some browsers only show the leave prompt when returnValue is set.
      event.returnValue = "";
    };

    // Captured on pagehide (not beforeunload) so it doesn't fire when the
    // member cancels the leave prompt; sendBeacon survives page teardown.
    const handlePageHide = () => {
      captureEvent(
        SurveyEvents.Dismissed,
        { survey_id: surveyId },
        { transport: "sendBeacon" },
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [hasAnswers, isCompleted, surveyId]);

  const setAnswer = useCallback(
    (questionId: string, value: SurveyAnswerValue) => {
      setAnswers((previousAnswers) => ({
        ...previousAnswers,
        [questionId]: value,
      }));

      if (!hasStartedRef.current) {
        hasStartedRef.current = true;
        captureEvent(SurveyEvents.Started, { survey_id: surveyId });
      }
    },
    [surveyId],
  );

  const captureQuestionAnswered = useCallback(
    (questionId: string, value: SurveyAnswerValue) => {
      if (Object.is(lastCapturedAnswersRef.current.get(questionId), value)) {
        return;
      }

      lastCapturedAnswersRef.current.set(questionId, value);

      const question = questions.find((question) => question.id === questionId);
      const shouldRedact =
        question === undefined || !isAutoAdvanceQuestion(question);

      captureEvent(SurveyEvents.QuestionAnswered, {
        survey_id: surveyId,
        question_id: questionId,
        ...(shouldRedact ? { answer_redacted: true } : { answer_value: value }),
      });
    },
    [questions, surveyId],
  );

  const cancelAutoAdvance = useCallback(() => {
    if (autoAdvanceTimerRef.current !== null) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, []);

  useEffect(() => cancelAutoAdvance, [cancelAutoAdvance]);

  const answerQuestion = useCallback(
    (questionId: string, value: SurveyAnswerValue) => {
      setAnswer(questionId, value);
      captureQuestionAnswered(questionId, value);

      cancelAutoAdvance();
      if (isLastStep) return;

      autoAdvanceTimerRef.current = setTimeout(() => {
        autoAdvanceTimerRef.current = null;
        setCurrentIndex((previousIndex) =>
          previousIndex < totalSteps - 1 ? previousIndex + 1 : previousIndex,
        );
      }, AUTO_ADVANCE_DELAY_MS);
    },
    [
      cancelAutoAdvance,
      captureQuestionAnswered,
      isLastStep,
      setAnswer,
      totalSteps,
    ],
  );

  const goBack = useCallback(() => {
    cancelAutoAdvance();
    setCurrentIndex((previousIndex) => Math.max(previousIndex - 1, 0));
  }, [cancelAutoAdvance]);

  const goForward = useCallback(() => {
    if (!canGoForward || isLastStep) return;

    cancelAutoAdvance();

    // Auto-advancing choices capture at selection; all other inputs wait for
    // navigation so editable answers emit only after confirmation.
    if (
      !isAutoAdvanceQuestion(currentQuestion) &&
      isAnsweredValue(currentAnswer)
    ) {
      captureQuestionAnswered(currentQuestion.id, currentAnswer);
    }

    setCurrentIndex((previousIndex) =>
      previousIndex < totalSteps - 1 ? previousIndex + 1 : previousIndex,
    );
  }, [
    cancelAutoAdvance,
    canGoForward,
    captureQuestionAnswered,
    currentAnswer,
    currentQuestion,
    isLastStep,
    totalSteps,
  ]);

  const submit = useCallback(() => {
    if (isPending || !canSubmit) return;

    // See goForward for why non-auto-advancing capture is deferred.
    if (
      !isAutoAdvanceQuestion(currentQuestion) &&
      isAnsweredValue(currentAnswer)
    ) {
      captureQuestionAnswered(currentQuestion.id, currentAnswer);
    }

    const responses = questions.reduce<SurveyResponse[]>((result, question) => {
      const value = answers[question.id];

      if (isAnsweredValue(value)) {
        result.push({ questionId: question.id, value });
      }

      return result;
    }, []);

    if (responses.length === 0) return;

    setHasSubmitError(false);
    mutateResponses(responses);
  }, [
    answers,
    canSubmit,
    captureQuestionAnswered,
    currentAnswer,
    currentQuestion,
    isPending,
    questions,
    mutateResponses,
  ]);

  const contextValue = useMemo<SessionSurveyContextType>(
    () => ({
      surveyId,
      sessionContext: details.context,
      questions,
      currentIndex,
      currentQuestion,
      totalSteps,
      answers,
      isLastStep,
      canGoBack,
      hasCurrentAnswer,
      canGoForward,
      canSubmit,
      isSubmitting: isPending,
      hasSubmitError,
      isCompleted,
      setAnswer,
      answerQuestion,
      goBack,
      goForward,
      submit,
    }),
    [
      answerQuestion,
      answers,
      canGoBack,
      canGoForward,
      canSubmit,
      currentIndex,
      currentQuestion,
      details.context,
      goBack,
      goForward,
      hasCurrentAnswer,
      hasSubmitError,
      isCompleted,
      isLastStep,
      questions,
      setAnswer,
      surveyId,
      submit,
      isPending,
      totalSteps,
    ],
  );

  return (
    <SessionSurveyContext.Provider value={contextValue}>
      {children}
    </SessionSurveyContext.Provider>
  );
};
